import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/solid';

const PDFControls = ({
  currentPage,
  totalPages,
  zoomLevel,
  isLoading,
  showHighlights,
  onPageChange,
  onZoomChange,
  onHighlightsToggle,
}) => {
  const [inputValue, setInputValue] = useState(currentPage);

  const controlBarStyles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F5F5F5',
      borderBottom: '1px solid #E5E5E5',
      padding: '8px 16px',
      gap: '8px',
    },
    group: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '0 8px',
      borderRight: '1px solid #E5E5E5',
    },
    lastGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '0 8px',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      padding: '6px',
      border: 'none',
      borderRadius: '4px',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    buttonHover: {
      backgroundColor: '#E5E5E5',
    },
    buttonDisabled: {
      opacity: 0.4,
      cursor: 'default',
    },
    input: {
      width: '25px',
      textAlign: 'center',
      fontSize: '13px',
      border: '1px solid #E5E5E5',
      borderRadius: '4px',
      padding: '4px',
      appearance: 'none', // Hide the up/down arrows
      MozAppearance: 'textfield', // For Firefox
    },
    text: {
      fontSize: '13px',
      fontWeight: 500,
      color: '#1A1A1A',
      minWidth: '60px',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      color: '#1A1A1A',
    },
    checkboxInput: {
      width: '14px',
      height: '14px',
      margin: 0,
      cursor: 'pointer',
    },
  };

  const MAX_ZOOM_LEVEL = 1.75;
  const MIN_ZOOM_LEVEL = 0.5;

  const handleInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setInputValue(value);
  };

  const handleInputBlur = () => {
    const page = Math.min(Math.max(Number(inputValue) || 1, 1), totalPages);
    onPageChange(page);
    setInputValue(page <= 0 ? 1 : (page > totalPages ? totalPages : page));
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const page = Math.min(Math.max(Number(inputValue) || 1, 1), totalPages);
      onPageChange(page);
      setInputValue(page <= 0 ? 1 : (page > totalPages ? totalPages : page));
    }
  };

  const handlePageChange = (newPage) => {
    onPageChange(newPage);
    setInputValue(newPage);
  };

  return (
    <div style={controlBarStyles.container}>
      {/* Page Navigation Group */}
      <div style={controlBarStyles.group}>
        <button
          type="button"
          onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1 || isLoading}
          style={{
            ...controlBarStyles.button,
            ...(currentPage === 1 || isLoading
              ? controlBarStyles.buttonDisabled
              : {}),
          }}
          onMouseOver={(e) => {
            if (currentPage !== 1 && !isLoading) {
              e.currentTarget.style.backgroundColor = '#E5E5E5';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Previous Page"
        >
          <ChevronLeftIcon
            aria-label="Previous Page"
            style={{ width: '20px', height: '20px', color: '#1A1A1A' }}
          />
        </button>

        <span style={controlBarStyles.text}>
          <input
            className="mb-0 text-inherit font-medium"
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            style={controlBarStyles.input}
            title="Jump to Page"
          />
          /
          <div>
            {totalPages}
          </div>
        </span>

        <button
          type="button"
          onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages || isLoading}
          style={{
            ...controlBarStyles.button,
            ...(currentPage === totalPages || isLoading
              ? controlBarStyles.buttonDisabled
              : {}),
          }}
          onMouseOver={(e) => {
            if (currentPage !== totalPages && !isLoading) {
              e.currentTarget.style.backgroundColor = '#E5E5E5';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Next Page"
        >
          <ChevronRightIcon
            aria-label="Next Page"
            style={{ width: '20px', height: '20px', color: '#1A1A1A' }}
          />
        </button>
      </div>

      {/* Zoom Controls Group */}
      <div style={controlBarStyles.group}>
        <button
          type="button"
          onClick={() => onZoomChange(Math.max(zoomLevel - 0.25, MIN_ZOOM_LEVEL))}
          disabled={zoomLevel <= MIN_ZOOM_LEVEL || isLoading}
          style={{
            ...controlBarStyles.button,
            ...(zoomLevel <= MIN_ZOOM_LEVEL || isLoading
              ? controlBarStyles.buttonDisabled
              : {}),
          }}
          onMouseOver={(e) => {
            if (zoomLevel > MIN_ZOOM_LEVEL && !isLoading) {
              e.currentTarget.style.backgroundColor = '#E5E5E5';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Zoom Out"
        >
          <MagnifyingGlassMinusIcon
            aria-label="Zoom Out"
            style={{ width: '20px', height: '20px', color: '#1A1A1A' }}
          />
        </button>

        <span style={controlBarStyles.text}>
          {(zoomLevel * 100).toFixed(0)}
          %
        </span>

        <button
          type="button"
          onClick={() => onZoomChange(Math.min(zoomLevel + 0.25, MAX_ZOOM_LEVEL))}
          disabled={zoomLevel >= MAX_ZOOM_LEVEL || isLoading}
          style={{
            ...controlBarStyles.button,
            ...(zoomLevel >= MAX_ZOOM_LEVEL || isLoading
              ? controlBarStyles.buttonDisabled
              : {}),
          }}
          onMouseOver={(e) => {
            if (zoomLevel < MAX_ZOOM_LEVEL && !isLoading) {
              e.currentTarget.style.backgroundColor = '#E5E5E5';
            }
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Zoom In"
        >
          <MagnifyingGlassPlusIcon
            aria-label="Zoom In"
            style={{ width: '20px', height: '20px', color: '#1A1A1A' }}
          />
        </button>
      </div>

      {/* Highlights Toggle Group */}
      <div style={controlBarStyles.lastGroup}>
        <label style={controlBarStyles.checkbox}>
          <input
            type="checkbox"
            checked={showHighlights}
            onChange={e => onHighlightsToggle(e.target.checked)}
            style={controlBarStyles.checkboxInput}
          />
          Highlights
        </label>
      </div>
    </div>
  );
};

PDFControls.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  zoomLevel: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  showHighlights: PropTypes.bool.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onZoomChange: PropTypes.func.isRequired,
  onHighlightsToggle: PropTypes.func.isRequired,
};

export default PDFControls;
